/**
 * tm/components/ConsultRemindBlock.tsx — PC 대시보드 「내일 동행상담 확인」 (2026-10-28 지도사 「하루 전에 팀장님께 일정 변동 · 강조할 부분 문자를 클릭 한 번으로 · 직원이 복사해 자기 카톡으로」)
 *  줄마다: 업체 · 시간 · 담당 팀장님 · 지난 기록 · [문자 보내기](지도사 이름 · 번호 · 담당 팀장님께 바로) · [복사](내 말투 → 내 카톡) · [글 보기](받는 분 고르기 · 글 고치기)
 */
import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { C, R, T } from './ui';
import { copyText, mainTarget, remindCopied, remindLine, remindList, remindSend } from '../consultRemind';
import type { RemindItem } from '../consultRemind';

const btn = (primary: boolean): CSSProperties => ({ height: 28, padding: '0 9px', borderRadius: R.sm, border: `1px solid ${primary ? C.accent : C.border}`, background: primary ? C.accent : C.white, color: primary ? C.white : C.text, fontSize: T.sm, fontWeight: 700, cursor: 'pointer', flexShrink: 0 });

export default function ConsultRemindBlock({ wrap, onToast }: { wrap: (title: string, count: number, body: ReactNode) => ReactNode; onToast: (m: string) => void }) {
  const [data, setData] = useState<{ date: string; list: RemindItem[] } | null>(null);
  const [open, setOpen] = useState<string>('');
  const [keys, setKeys] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const load = useCallback(async () => { const r = await remindList().catch(() => null); setData(r && r.data ? r.data : { date: '', list: [] }); }, []);
  useEffect(() => { void load(); }, [load]);
  if (!data) return null;
  const list = data.list;

  const send = async (it: RemindItem, k?: string[], t?: string) => {
    if (busy) return;
    setBusy(it.id);
    try {
      const r = await remindSend(it.id, { keys: k, text: t });
      if (!r.success || !r.data) throw new Error(r.error || '보내지 못했어요');
      const okN = r.data.remind.results.filter(x => x.sent).length;
      onToast(okN ? `팀장님께 확인 문자를 보냈어요${okN < r.data.remind.results.length ? ' · 일부는 못 보냈어요' : ''}` : '문자를 보내지 못했어요 · 이유를 봐 주세요');
      setOpen(''); await load();
    } catch (e) { onToast(e instanceof Error ? e.message : '보내지 못했어요'); }
    finally { setBusy(''); }
  };
  const copy = async (it: RemindItem, body: string) => {
    if (await copyText(body)) { onToast('복사했어요 · 카톡에 붙여 넣어 보내 주세요'); void remindCopied(it.id).then(load); }
    else { setOpen(it.id); onToast('아래 글을 길게 눌러 복사해 주세요'); }
  };

  return <>{wrap('📞 내일 동행상담 확인', list.length, list.length === 0
    ? <div style={{ padding: '12px 14px', fontSize: T.sm, color: C.muted }}>내일 잡힌 동행상담이 없어요</div>
    : list.map((it, i) => {
      const main = mainTarget(it);
      const line = remindLine(it.remind, it.targets);
      const isOpen = open === it.id;
      return (
        <div key={it.id} style={{ padding: '10px 14px', borderBottom: i < list.length - 1 ? `1px solid ${C.border}` : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: T.md, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.company_name}</div>
              <div style={{ fontSize: T.xs, color: line && line.bad ? C.red : C.sub, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[it.time, main ? main.honor : '받을 팀장님 없음', line ? line.text : ''].filter(Boolean).join(' · ')}
              </div>
              {main && <button type="button" onClick={() => { if (isOpen) { setOpen(''); return; } setOpen(it.id); setKeys([main.key]); setText(main.sms); }}
                style={{ border: 'none', background: 'none', padding: 0, marginTop: 3, color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>{isOpen ? '접기' : it.remind ? '글 보기 · 다시 보내기 ›' : '글 보기 · 받는 분 고르기 ›'}</button>}
            </div>
            {main && !main.why && !it.remind && <button type="button" style={btn(true)} disabled={busy === it.id} onClick={() => void send(it)}>{busy === it.id ? '보내는 중' : '문자 보내기'}</button>}
            {main && <button type="button" style={btn(false)} onClick={() => void copy(it, main.copy)}>복사</button>}
                      </div>
          {isOpen && main && (
            <div style={{ marginTop: 8, padding: 10, borderRadius: R.md, background: '#F8FAFC', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 6, fontSize: T.sm }}>
                {it.targets.map(t => (
                  <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 4, color: t.why ? C.muted : C.text }}>
                    <input type="checkbox" disabled={!!t.why} checked={keys.includes(t.key)} onChange={e => setKeys(k => (e.target.checked ? [...k, t.key] : k.filter(x => x !== t.key)))} />
                    {t.honor}{t.why ? ` · ${t.why}` : ''}
                  </label>
                ))}
              </div>
              <textarea value={text} onChange={e => setText(e.target.value)} rows={7} style={{ width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: R.sm, border: `1px solid ${C.border}`, fontSize: T.sm, lineHeight: 1.6, fontFamily: 'inherit', resize: 'vertical' }} />
              <div style={{ fontSize: T.xs, color: C.muted, margin: '4px 0 8px' }}>고치지 않으면 받는 분마다 이름을 넣어 보내요 · 글을 고치면 고른 분 모두에게 같은 글이 가요 · 문자는 지도사 번호로 나가요</div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button type="button" style={btn(false)} onClick={() => void copy(it, text)}>이 글 복사</button>
                <button type="button" style={btn(true)} disabled={!keys.length || busy === it.id} onClick={() => void send(it, keys, text === main.sms ? undefined : text)}>보내기</button>
              </div>
            </div>
          )}
        </div>
      );
    }))}</>;
}
